import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {FONT_PROXIMA_TITLE, LEGACY_FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureChip from 'ui-library/chips/ProcedureChip';
import {GRAY_DARKER, NEAR_BLACK, GRAY_SEMIDARK} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';

import EditProcedureModal, {ProcedureChangeParams} from './EditProcedureModal';

interface SelectedProcedureEditorProps {
  procedureId: string;
  flowchartVersionId: string;
  procedureType: ProcedureType;
  studyType: StudyType;
  // Only invoked when isEditable is true, so read-only callers can omit it.
  onProcedureChange?: (params: ProcedureChangeParams) => Promise<void>;
  isEditable: boolean;
}

export default function SelectedProcedureEditor(props: SelectedProcedureEditorProps): JSX.Element {
  const procedureUIStringLowercase = getProcedureUIString(
    ProceduresStringKeys.PROCEDURE,
    props.procedureType,
    {stringCase: StringCase.LOWER}
  );
  return (
    <div className={css(styles.settingContainer)}>
      <div className={css(styles.settingTitle)}>
        {`Active ${getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.procedureType, {
          stringCase: StringCase.LOWER,
        })}`}
      </div>
      {props.isEditable && (
        <div className={css(styles.settingDescription)}>
          {`Select ${procedureUIStringLowercase} to design. Changing the ${procedureUIStringLowercase} will clear existing values in your design.`}
        </div>
      )}
      <div
        className={css(styles.procedureChipContainer)}
        {...getTestProps({component: 'ProcedureChip', element: 'root'})}
      >
        <ProcedureChip
          id={props.procedureId}
          flowchartVersionId={props.flowchartVersionId}
          size={ChipSize.SMALL}
        />
        {props.isEditable && (
          <BenchlingIcon
            icon="edit"
            color={GRAY_SEMIDARK}
            onClick={async () => {
              await legacyRunModal(EditProcedureModal, {
                procedureId: props.procedureId,
                flowchartVersionId: props.flowchartVersionId,
                procedureType: props.procedureType,
                studyType: props.studyType,
                excludeLegacyProcedures: true,
                onProcedureChange: props.onProcedureChange,
                onRequestClose: () => {},
              });
            }}
            style={{fontSize: '13px', cursor: 'pointer'}}
          />
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  settingContainer: {display: 'flex', flexDirection: 'column', padding: 12},
  settingTitle: {...FONT_PROXIMA_TITLE, color: NEAR_BLACK, marginBottom: 8},
  settingDescription: {...LEGACY_FONT_PROXIMA_13, color: GRAY_DARKER, marginBottom: 12},
  procedureChipContainer: {display: 'flex', flexDirection: 'row', alignItems: 'center'},
});
